import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Instagram, CheckCircle2, XCircle, Loader2, RefreshCw, LogOut } from "lucide-react";
import { toast } from "sonner";

/**
 * Iter49 P0 — Instagram OAuth completion page.
 * Mirrors the Facebook callback flow but scoped to Instagram Business
 * accounts linked through the user's Facebook Pages.
 */
export default function InstagramOAuthCallback() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [phase, setPhase] = useState("exchanging");
  const [accounts, setAccounts] = useState([]);
  const [userToken, setUserToken] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [selecting, setSelecting] = useState(null);
  const [connection, setConnection] = useState(null);

  useEffect(() => {
    (async () => {
      const err = params.get("error") || params.get("error_reason");
      const desc = params.get("error_description") || "";
      if (err) {
        setErrorMsg(`Meta returned "${err}"${desc ? ` — ${desc}` : ""}. Retry from Business Settings.`);
        setPhase("error");
        return;
      }
      const code = params.get("code");
      const state = params.get("state");
      const savedState = sessionStorage.getItem("ig_oauth_state");
      if (!code) {
        setErrorMsg("Missing OAuth code. Return to Business Settings and start again.");
        setPhase("error");
        return;
      }
      if (savedState && state && state !== savedState) {
        setErrorMsg("OAuth state mismatch — possible CSRF. Start again from Business Settings.");
        setPhase("error");
        return;
      }
      try {
        const { data } = await api.post("/instagram/oauth/callback", { code, state });
        const eligible = data.accounts || [];
        if (eligible.length === 0) {
          setErrorMsg("No Instagram Business/Creator account is linked to any of your Facebook Pages. Convert to a Business/Creator account and link it to a Page, then retry.");
          setPhase("error");
          return;
        }
        setAccounts(eligible);
        setUserToken(data.user_access_token);
        setPhase("selecting");
      } catch (e) {
        const msg = e.response?.data?.detail || "Instagram token exchange failed — retry from Business Settings.";
        setErrorMsg(msg);
        setPhase("error");
      } finally {
        sessionStorage.removeItem("ig_oauth_state");
      }
    })();
  }, [params]);

  const selectAccount = async (a) => {
    setSelecting(a.ig_user_id);
    try {
      const { data } = await api.post("/instagram/accounts/select", {
        ig_user_id: a.ig_user_id,
        ig_username: a.ig_username,
        linked_page_id: a.linked_page_id,
        linked_page_name: a.linked_page_name,
        user_access_token: userToken,
      });
      setConnection({ ...a, ...data });
      setPhase("success");
      toast.success(`Connected to Instagram: @${a.ig_username}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Account selection failed — try again");
    } finally { setSelecting(null); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect this Instagram account? Existing published media will remain on Instagram.")) return;
    try {
      await api.post("/instagram/disconnect");
      toast.success("Disconnected from Instagram");
      nav("/app/settings");
    } catch { toast.error("Disconnect failed"); }
  };

  return (
    <div className="max-w-2xl mx-auto p-8" data-testid="instagram-oauth-callback-page">
      <div className="flex items-center gap-3 mb-6">
        <Instagram className="w-6 h-6 text-fuchsia-600"/>
        <h1 className="font-heading text-2xl font-bold">Instagram connection</h1>
      </div>

      {phase === "exchanging" && (
        <div className="bg-white border border-zinc-200 rounded-xl p-8 text-center card-crisp" data-testid="ig-oauth-exchanging">
          <Loader2 className="w-8 h-8 animate-spin text-[#FF5A00] mx-auto mb-3"/>
          <div className="font-medium">Verifying with Meta…</div>
          <div className="text-sm text-zinc-500 mt-1">Enumerating eligible Instagram Business accounts.</div>
        </div>
      )}

      {phase === "selecting" && (
        <div className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp" data-testid="ig-oauth-account-picker">
          <div className="font-medium mb-1">Select the Instagram account LeadForge should publish to</div>
          <div className="text-sm text-zinc-500 mb-4">Only Business/Creator accounts linked to your Facebook Pages are shown. Tokens are encrypted server-side.</div>
          <div className="space-y-2">
            {accounts.map((a) => (
              <button
                key={a.ig_user_id}
                onClick={() => selectAccount(a)}
                disabled={!!selecting}
                className="w-full text-left border border-zinc-200 rounded-lg p-3 hover:border-fuchsia-500 hover:bg-fuchsia-50 transition-colors flex items-center justify-between"
                data-testid={`ig-account-option-${a.ig_user_id}`}
              >
                <div>
                  <div className="font-medium">@{a.ig_username || a.ig_user_id}</div>
                  <div className="text-xs text-zinc-500 font-mono-accent">
                    {a.ig_account_type} · Linked to Page “{a.linked_page_name}”
                  </div>
                </div>
                {selecting === a.ig_user_id ? <Loader2 className="w-4 h-4 animate-spin"/> : <span className="text-fuchsia-600 text-sm">Select →</span>}
              </button>
            ))}
          </div>
        </div>
      )}

      {phase === "success" && (
        <div className="bg-white border border-emerald-200 rounded-xl p-6 card-crisp" data-testid="ig-oauth-success">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-600"/>
            <div className="font-medium">Connected!</div>
          </div>
          <div className="text-sm text-zinc-600 mb-4">
            LeadForge is now linked to <span className="font-medium">@{connection?.ig_username}</span>.
            Approved social posts can be published to Instagram from the job page.
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={() => nav("/app/settings")} data-testid="ig-oauth-done">
              Back to Settings
            </Button>
            <Button variant="outline" onClick={() => nav(0)} data-testid="ig-oauth-change">
              <RefreshCw className="w-4 h-4 mr-1"/> Change Account
            </Button>
            <Button variant="outline" onClick={disconnect} data-testid="ig-oauth-disconnect">
              <LogOut className="w-4 h-4 mr-1"/> Disconnect
            </Button>
          </div>
        </div>
      )}

      {phase === "error" && (
        <div className="bg-white border border-red-200 rounded-xl p-6 card-crisp" data-testid="ig-oauth-error">
          <div className="flex items-center gap-2 mb-2">
            <XCircle className="w-5 h-5 text-red-600"/>
            <div className="font-medium">Couldn't finish the connection</div>
          </div>
          <div className="text-sm text-zinc-600 mb-4">{errorMsg}</div>
          <Button onClick={() => nav("/app/settings")}>Back to Settings</Button>
        </div>
      )}
    </div>
  );
}
